import { FC, useContext, useEffect, useRef } from "react";
import { Controller } from "react-hook-form";
import AsyncSelect from "react-select/async";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import { createLoadOptions, useDebounce } from "../../hooks/useDebounce";
import { useI18n } from "../../hooks/useTranslations";
import { usePermissionsApi } from "../../http/permission/api";
import { useRolesApi } from "../../http/roles/api";
import useUserApi from "../../http/user/api";
import { User } from "../../http/user/type";
import Form from "../ui/Form";
import FormField from "../ui/FormField";
import Input from "../ui/Input";
import MultiCheckbox from "../ui/MultiCheckbox";
import reactSelectStyles from "../ui/ReactSelectUtils";

export interface UserFormBody {
  user: User | null;
}

type UsersFormProps = {
  user?: User;
  onSubmit: (data: UserFormBody) => void;
  isDisabled?: boolean;
  isLoading?: boolean;
};

const UserForm: FC<UsersFormProps> = ({ user, onSubmit, isDisabled, isLoading }) => {
  const i18n = useI18n();
  const userApi = useUserApi();
  const rolesApi = useRolesApi();
  const permissionsApi = usePermissionsApi();
  const dispatch = useContext(NotificationDispatch);

  const rolesSwr = rolesApi.useRoles();
  const permissionsSwr = permissionsApi.usePermissions();

  useEffect(() => {
    rolesSwr.error && dispatch(showErrorNotification(i18n, rolesSwr.error));
    permissionsSwr.error && dispatch(showErrorNotification(i18n, permissionsSwr.error));
  }, [rolesSwr.error, permissionsSwr.error]);

  const allRoles =
    rolesSwr.data?.items.map((x) => ({
      name: x.name,
      permissions: x.permissions,
    })) ?? [];

  const allPermissions =
    permissionsSwr.data?.items.map((x) => ({
      name: x.name,
    })) ?? [];

  const defaultValues = {
    user: user ?? null,
  };

  return (
    <Form<UserFormBody>
      defaultValues={defaultValues}
      submitText={i18n.t.common.save}
      disabled={isDisabled}
      onSubmit={() => onSubmit}
    >
      {({ control, setValue, watch, reset }) => {
        const roles = watch("user")?.roles ?? [];
        const permissions = watch("user")?.permissions ?? [];

        useEffect(() => {
          user && reset({ user });
        }, [user?.id]);

        return (
          <>
            <Controller
              name="user"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.t.common.email} error={fieldState.error}>
                  {({ labelId, isInvalid }) => {
                    const searchTerm = useRef("");
                    const debouncedValue = useDebounce(searchTerm);
                    const aborter = useRef<AbortController | undefined>(undefined);
                    const loadOptions = createLoadOptions<User>(aborter, debouncedValue, userApi.searchNewUsers);

                    const conditionalLoadOptions = async (inputValue: string): Promise<User[]> => {
                      if (inputValue.trim().length < 2) {
                        return [];
                      }

                      return loadOptions(inputValue);
                    };

                    return (
                      <AsyncSelect<User>
                        {...field}
                        inputId={labelId}
                        loadOptions={conditionalLoadOptions}
                        getOptionLabel={(o) => `${o.email}`}
                        getOptionValue={(o) => o.id}
                        placeholder={i18n.t.common.select + "..."}
                        isDisabled={isDisabled || isLoading || !!user?.email}
                        styles={reactSelectStyles(isInvalid)}
                        onChange={(user) => setValue("user", user)}
                      />
                    );
                  }}
                </FormField>
              )}
            />
            <FormField label={i18n.t.common.firstName}>
              <Input disabled value={watch("user")?.firstName ?? ""} />
            </FormField>
            <FormField label={i18n.t.common.lastName}>
              <Input disabled value={watch("user")?.lastName ?? ""} />
            </FormField>

            <FormField label={i18n.t.common.roles}>
              <MultiCheckbox
                values={allRoles.map((x) => ({
                  id: x.name,
                  title: x.name,
                  selected: roles?.some((r) => r.name === x.name),
                }))}
                disabled={isDisabled || isLoading}
                onUpdate={(name, checked) => {
                  const role = allRoles.find((r) => r.name === name);
                  if (!role) return;
                  setValue("user.roles", checked ? [...roles, role] : roles.filter((r) => r.name !== name));
                }}
              />
            </FormField>

            <FormField label={i18n.t.common.permissions}>
              <MultiCheckbox
                values={allPermissions.map((x) => ({
                  id: x.name,
                  title: x.name,
                  selected: permissions?.some((p) => p.name === x.name),
                }))}
                disabled={isDisabled || isLoading}
                onUpdate={(name, checked) => {
                  const permission = allPermissions.find((p) => p.name === name);
                  if (!permission) return;
                  setValue(
                    "user.permissions",
                    checked ? [...permissions, permission] : permissions.filter((p) => p.name !== name)
                  );
                }}
              />
            </FormField>
          </>
        );
      }}
    </Form>
  );
};
export default UserForm;
